import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Typography } from '@gapartners/ui';
import {
  getDesktopNotificationPermission,
  requestDesktopNotificationPermission,
  type DesktopNotificationPermission,
} from '@/utils/desktopNotification';

/**
 * Floating prompt shown once while the browser notification permission is still
 * 'default'. Clicking "Enable" runs requestPermission() inside a user gesture so
 * the browser reliably shows its permission dialog. Hidden after the user
 * enables, denies, or dismisses.
 */
export function DesktopNotificationPrompt() {
  const { t } = useTranslation();
  const [permission, setPermission] = useState<DesktopNotificationPermission>(
    () => getDesktopNotificationPermission()
  );
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || permission !== 'default') {
    return null;
  }

  const handleEnable = async () => {
    const result = await requestDesktopNotificationPermission();
    setPermission(result);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 2000,
        maxWidth: 360,
      }}
    >
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {t('components.desktopNotification.promptTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('components.desktopNotification.promptBody')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="text"
            color="inherit"
            onClick={() => setDismissed(true)}
          >
            {t('components.desktopNotification.notNow')}
          </Button>
          <Button variant="contained" color="primary" onClick={handleEnable}>
            {t('components.desktopNotification.enable')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
